import { throwErrorWithId } from '@pypestream/utils';

import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';
import { getBotWithAssets } from '../../utils';
import { loadAgentManagerAssets } from '../../utils/load-agent-manager-assets';
import { readLegacyBotSolutionId } from '../../utils/resolve-bot-solution-id';

export const updateAssets = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{
  selectedBot: ProStudioContext['selectedBot'];
  cb?: (assets: { id: string; url: string }[]) => void;
} | void> => {
  let callback: ((assets: { id: string; url: string }[]) => void) | undefined;

  if (event.type === 'updateAssets') {
    callback = event.cb;
  }

  try {
    const { selectedBot, project } = ctx;

    const projectId = project?.id;

    if (!projectId) {
      throwErrorWithId('Project ID is required');
      return;
    }

    if (!selectedBot) {
      throwErrorWithId('Selected bot is required');
      return;
    }

    // An Agent Manager v2 solution has no Bot Manager counterpart (no
    // `botSolutionId`), so its media is listed from the solution repo's
    // `assets/` folder instead — the same read `loadBotVersions` makes.
    const solutionId = selectedBot.uniqueId;
    if (!readLegacyBotSolutionId(ctx) && solutionId && ctx.draftVersion) {
      return {
        selectedBot: {
          ...selectedBot,
          assets: await loadAgentManagerAssets(solutionId, ctx.draftVersion),
        },
        cb: callback,
      };
    }

    const botWithAssets = await getBotWithAssets({
      selectedBot,
      projectId,
    });

    return {
      selectedBot: botWithAssets,
      cb: callback,
    };
  } catch (error) {
    // Guarantee the caller's cb settles exactly once even when the asset fetch
    // (or a guard) throws — otherwise a caller awaiting it would hang forever.
    // We still re-throw so the machine's onError transition (#botFlowError)
    // fires as before (PSTUDIO-3710).
    callback?.([]);
    throw error;
  }
};
